// src/components/pages/backtest/partials/PerformanceTableBacktest.js

import React, { useState } from "react";
import styles from "./style/PerformanceTableBacktest.module.css";
import { useTranslation } from "react-i18next";

const InfoIcon = () => (
  <svg 
    width="15" height="15" viewBox="0 0 24 24" 
    fill="none" stroke="currentColor" strokeWidth="2" 
    strokeLinecap="round" strokeLinejoin="round"
    style={{ color: '#9ca3af', cursor: 'help' }}
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

function PerformanceTableBacktest({ optimizedMetrics, benchmarkMetrics, benchmarkSymbol }) {
  const [showExpertMetrics, setShowExpertMetrics] = useState(false);
  const [showMddHistory, setShowMddHistory] = useState(false);
  const { t } = useTranslation();

  // 1. 지표별 절대 평가 기준 (Thresholds) 정의 함수
  const getMetricStatus = (type, value) => {
    // 값이 없거나 숫자가 아니면 null 반환
    if (value === undefined || value === null || isNaN(value)) return null;

    switch (type) {
      case 'winRate': // 승률 (0.55 = 55%)
        if (value >= 0.55) return { label: 'High', color: '#166534', bg: '#dcfce7' }; // Green
        if (value < 0.40) return { label: 'Low', color: '#991b1b', bg: '#fee2e2' };   // Red
        return null; // Normal

      case 'sharpe': // 샤프 지수
        if (value >= 2.0) return { label: 'Excellent', color: '#1e40af', bg: '#dbeafe' }; // Blue
        if (value >= 1.0) return { label: 'Good', color: '#166534', bg: '#dcfce7' };    // Green
        if (value < 0.5) return { label: 'Bad', color: '#991b1b', bg: '#fee2e2' };      // Red
        return null;

      case 'profitFactor': // 프로핏 팩터
        if (value >= 1.5) return { label: 'Good', color: '#166534', bg: '#dcfce7' };
        if (value < 1.0) return { label: 'Loss', color: '#991b1b', bg: '#fee2e2' };
        return null;

      case 'mdd': // 최대 낙폭 (0.15 = 15%) - 낮을수록 좋음
        // MDD는 보통 양수(절대값)로 넘어온다고 가정 (예: 0.21)
        if (value < 0.15) return { label: 'Stable', color: '#1e40af', bg: '#dbeafe' };  // Blue (안정적)
        if (value > 0.30) return { label: 'Risky', color: '#991b1b', bg: '#fee2e2' };   // Red (위험)
        return null;

      case 'var95': // VaR (보통 음수로 옴, 예: -0.015)
        // 절대값이 작을수록 좋음
        const absVar = Math.abs(value);
        if (absVar <= 0.015) return { label: 'Safe', color: '#166534', bg: '#dcfce7' };
        if (absVar >= 0.025) return { label: 'Risky', color: '#991b1b', bg: '#fee2e2' };
        return null;

      case 'sortino': // 소르티노
        if (value >= 2.0) return { label: 'Excellent', color: '#1e40af', bg: '#dbeafe' };
        if (value >= 1.0) return { label: 'Good', color: '#166534', bg: '#dcfce7' };
        if (value < 0.5) return { label: 'Bad', color: '#991b1b', bg: '#fee2e2' };
        return null;

      case 'calmar': // 칼마
        if (value >= 1.5) return { label: 'Good', color: '#166534', bg: '#dcfce7' };
        if (value < 0.5) return { label: 'Bad', color: '#991b1b', bg: '#fee2e2' };
        return null;

      case 'ulcer': // 고통 지수 (낮을수록 좋음)
        if (value < 5.0) return { label: 'Low Stress', color: '#166534', bg: '#dcfce7' };
        if (value > 10.0) return { label: 'High Stress', color: '#991b1b', bg: '#fee2e2' };
        return null;

      case 'upi': // UPI (높을수록 좋음)
        if (value >= 2.0) return { label: 'Good', color: '#166534', bg: '#dcfce7' };
        if (value < 1.0) return { label: 'Bad', color: '#991b1b', bg: '#fee2e2' };
        return null;

      case 'recovery': // 평균 회복일 (일수)
        if (value < 20) return { label: 'Fast', color: '#1e40af', bg: '#dbeafe' };
        if (value > 60) return { label: 'Slow', color: '#991b1b', bg: '#fee2e2' };
        return null;

      default:
        return null;
    }
  };

  // 2. 배지 렌더링 헬퍼 함수
  const renderBadge = (type, value) => {
    const status = getMetricStatus(type, value);
    if (!status) return null;

    return (
      <span style={{
        fontSize: '0.7rem',
        marginLeft: '6px',
        padding: '2px 6px',
        borderRadius: '4px',
        fontWeight: '600',
        color: status.color,
        backgroundColor: status.bg,
        verticalAlign: 'middle'
      }}>
        {status.label}
      </span>
    );
  };

  // 툴팁 텍스트 정의
  const explanations = {
    winRate: t("backtest.winRateExplanation") || "전체 기간 중 수익을 낸 기간의 비율입니다.",
    profitFactor: t("backtest.profitFactorExplanation") || "총 수익 / 총 손실 비율입니다. 1.5 이상이면 훌륭합니다.",
    ulcer: t("backtest.ulcerExplanation") || "하락의 깊이와 기간을 모두 고려한 스트레스 지수입니다. (5 미만 우수)",
    upi: t("backtest.upiExplanation") || "Ulcer Index를 반영한 위험 조정 수익률입니다. (2.0 이상 우수)",
    sharpe: t("backtest.sharpePerformancetable") || "변동성 대비 초과 수익률입니다. (1.0 이상 우수)",
    calmar: t("backtest.calmarPerformancetable") || "최근 3년 연평균 수익률을 MDD로 나눈 값입니다. (1.5 이상 우수)",
    sortino: t("backtest.sortinoPerformancetable") || "하방 변동성(손실 위험)만 고려한 효율성 지표입니다.",
    avgRecovery: t("backtest.avgRecoveryExplanation") || "고점 회복까지 걸린 평균 기간입니다. (20일 미만 우수)",
    avgDrawdown: t("backtest.avgDrawdownExplanation") || "발생한 모든 하락 구간의 평균 깊이입니다.",
    diversification: t("backtest.diversificationExplanation") || "높을수록 좋습니다. 자산 간 상관관계를 낮춰 위험을 얼마나 잘 분산했는지 보여줍니다. (>1.2 우수)",
    var: t("backtest.varExplanation") || "VaR (95%): 통계적으로 내일 발생할 수 있는 최대 예상 손실입니다.",
  };

  const renderTooltip = (text) => (
    <span className={styles.infoIconTooltipContainer}>
      <InfoIcon />
      <span className={styles.customTooltip}>{text}</span>
    </span>
  );

  // 섹션 헤더 스타일
  const sectionHeaderStyle = {
    background: "#f9fafb",
    color: "#4b5563",
    fontSize: "0.85rem",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    padding: "10px 16px",
    borderBottom: "1px solid #e5e7eb"
  };

  return (
    <div className={styles.performanceTableContainer}>
      <table className={styles.performanceTable}>
        <thead>
          <tr>
            <th style={{ width: "40%" }}></th>
            <th style={{ width: "30%" }}>{t("backtest.strategy") || "내 포트폴리오 (Strategy)"}</th>
            <th style={{ width: "30%" }}>{t("backtest.benchmark", { benchmarkSymbol }) || "벤치마크 (Benchmark)"}</th>
          </tr>
        </thead>
        <tbody>
          
          {/* =========================================
              SECTION 1: 📈 성과 요약 (Profitability)
          ========================================= */}
          <tr>
            <td colSpan="3" style={sectionHeaderStyle}>
              📈 {t("backtest.profitabilitySummary") || "성과 요약 (Profitability)"}
            </td>
          </tr>
          
          {/* 누적 수익률 */}
          <tr>
            <td><strong>{t("backtest.cumulativeReturn")}</strong></td>
            <td className={`${styles.highlight} ${optimizedMetrics.cumulativeReturn > 0 ? styles.textGreen : styles.textRed}`}>
              {(optimizedMetrics.cumulativeReturn * 100).toFixed(2)}%
              {optimizedMetrics.cumulativeReturn > benchmarkMetrics.cumulativeReturn && <span style={{fontSize: '0.8em', marginLeft: '4px'}}>🟢</span>}
            </td>
            <td>{(benchmarkMetrics.cumulativeReturn * 100).toFixed(2)}%</td>
          </tr>

          {/* CAGR */}
          <tr>
            <td><strong>{t("backtest.cagr")}</strong></td>
            <td style={{ fontWeight: 600 }}>{(optimizedMetrics.cagr * 100).toFixed(2)}%</td>
            <td>{(benchmarkMetrics.cagr * 100).toFixed(2)}%</td>
          </tr>

          {/* 승률 (Win Rate) */}
          <tr>
            <td>
               {t("backtest.winRate") || "승률 (Win Rate)"}
               {renderTooltip(explanations.winRate)}
            </td>
            <td>
              {(optimizedMetrics.winRate * 100).toFixed(1)}%
              {renderBadge('winRate', optimizedMetrics.winRate)}
            </td>
            <td>{(benchmarkMetrics.winRate * 100).toFixed(1)}%</td>
          </tr>


          {/* =========================================
              SECTION 2: 🛡️ 위험 관리 (Risk & Diversification)
          ========================================= */}
          <tr>
            <td colSpan="3" style={sectionHeaderStyle}>
              🛡️ {t("backtest.riskManagement") || "위험 관리 및 분산 (Risk & Diversification)"}
            </td>
          </tr>

          {/* 분산 효율 점수 (DR) */}
          <tr>
            <td>
              <strong>{t("backtest.diversificationRatio") || "분산 효율 점수 (DR)"}</strong>
              {renderTooltip(explanations.diversification)}
            </td>
            <td className={optimizedMetrics.diversificationRatio >= 1.2 ? styles.textGreen : ""}>
              <strong>{(optimizedMetrics.diversificationRatio || 1.0).toFixed(2)}x</strong>
              {/* 기존 로직 유지 (또는 renderBadge('diversification', val)을 만들어 대체 가능) */}
              {optimizedMetrics.diversificationRatio >= 1.2 && (
                <span style={{ fontSize: '0.7rem', marginLeft: '6px', background: '#dcfce7', color:'#166534', padding:'2px 6px', borderRadius:'4px', fontWeight: '600' }}>
                  Good
                </span>
              )}
            </td>
            <td>{(benchmarkMetrics.diversificationRatio || 1.0).toFixed(2)}x</td>
          </tr>

          {/* 최대 하락폭 (MDD) */}
          <tr 
            onClick={() => setShowMddHistory(!showMddHistory)} 
            style={{ cursor: "pointer" }}
            className={styles.rowHover}
          >
            <td>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <strong>{t("backtest.maxDrawdown")}</strong>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', background:'#f3f4f6', padding:'2px 6px', borderRadius:'4px' }}>
                  {showMddHistory ? t("backtest.collapse") || "접기 ▲" : t("backtest.history") || "히스토리 ▼"}
                </span>
              </div>
            </td>
            <td className={styles.textRed} style={{ fontWeight: 700 }}>
              {(optimizedMetrics.maxDrawdown * 100).toFixed(2)}%
              {renderBadge('mdd', optimizedMetrics.maxDrawdown)}
            </td>
            <td>{(benchmarkMetrics.maxDrawdown * 100).toFixed(2)}%</td>
          </tr>

          {/* MDD 확장 영역 */}
          {showMddHistory && optimizedMetrics.mddHistory?.map((item, index) => (
            <tr key={`mdd-${index}`} style={{ backgroundColor: '#fff1f2' }}>
              <td style={{ paddingLeft: "24px", fontSize: "0.85rem", color: "#881337" }}>
                 ↳ {item.period}
              </td>
              <td colSpan="2" style={{ textAlign: "left", color: "#9f1239", fontSize: "0.85rem" }}>
                <span style={{ fontWeight: "bold", marginRight: "8px" }}>{(item.depth * 100).toFixed(2)}%</span>
                <span>{item.status === "Recovered" ? `(${item.recoveryDays} days)` : "⚠️ Ongoing"}</span>
              </td>
            </tr>
          ))}

          {/* 일간 예상 손실 (VaR) */}
          <tr>
            <td>
              <strong>{t("backtest.var95") || "일간 예상 손실 (VaR 95%)"}</strong>
              {renderTooltip(explanations.var)}
            </td>
            <td>
              {((optimizedMetrics.var95 || 0) * 100).toFixed(2)}%
              {renderBadge('var95', optimizedMetrics.var95)}
            </td>
            <td>{((benchmarkMetrics.var95 || 0) * 100).toFixed(2)}%</td>
          </tr>


          {/* =========================================
              SECTION 3: 📊 투자 효율성 (Efficiency)
          ========================================= */}
          <tr>
            <td colSpan="3" style={sectionHeaderStyle}>
              📊 {t("backtest.efficiency") || "투자 효율성 (Efficiency)"}
            </td>
          </tr>

          {/* 샤프 지수 */}
          <tr>
            <td>
              <strong>{t("backtest.sharpeRatio")}</strong>
              {renderTooltip(explanations.sharpe)}
            </td>
            <td style={{ fontWeight: optimizedMetrics.sharpeRatio > 1.0 ? 700 : 400 }}>
              {optimizedMetrics.sharpeRatio.toFixed(2)}
              {renderBadge('sharpe', optimizedMetrics.sharpeRatio)}
            </td>
            <td>{benchmarkMetrics.sharpeRatio.toFixed(2)}</td>
          </tr>

          {/* 프로핏 팩터 */}
          <tr>
            <td>
              {t("backtest.profitFactor") || "프로핏 팩터 (Profit Factor)"}
              {renderTooltip(explanations.profitFactor)}
            </td>
            <td>
              {optimizedMetrics.profitFactor.toFixed(2)}
              {renderBadge('profitFactor', optimizedMetrics.profitFactor)}
            </td>
            <td>{benchmarkMetrics.profitFactor.toFixed(2)}</td>
          </tr>


          {/* =========================================
              SECTION 4: 🔍 하락장 정밀 진단 (Advanced Toggle)
          ========================================= */}
          <tr 
            onClick={() => setShowExpertMetrics(!showExpertMetrics)} 
            className={styles.expertToggleBtn} 
            style={{ cursor: 'pointer', borderTop: '2px solid #e5e7eb' }}
          >
            <td colSpan="3" style={{ textAlign: 'center', padding: '14px', color: '#4b5563', fontWeight: 600, background: '#f9fafb' }}>
              {showExpertMetrics 
                ? t("backtest.expertViewLess") || "🔍 하락장 정밀 진단 접기 (Simple View) ▲" 
                : t("backtest.expertViewMore") || "🔍 하락장 정밀 진단 더보기 (Advanced Risk Analysis) ▼"}
            </td>
          </tr>

          {showExpertMetrics && (
            <>
              <tr>
                <td>{t("backtest.sortinoRatio") || "소티노 지수 (Sortino)"} {renderTooltip(explanations.sortino)}</td>
                <td>
                  {optimizedMetrics.sortinoRatio.toFixed(2)}
                  {renderBadge('sortino', optimizedMetrics.sortinoRatio)}
                </td>
                <td>{benchmarkMetrics.sortinoRatio.toFixed(2)}</td>
              </tr>
              <tr>
                <td>{t("backtest.calmarRatio") || "칼마 지수 (Calmar)"} {renderTooltip(explanations.calmar)}</td>
                <td>
                  {optimizedMetrics.calmarRatio.toFixed(2)}
                  {renderBadge('calmar', optimizedMetrics.calmarRatio)}
                </td>
                <td>{benchmarkMetrics.calmarRatio.toFixed(2)}</td>
              </tr>
              <tr>
                <td>{t("backtest.ulcer") || "고통 지수 (Ulcer Index)"} {renderTooltip(explanations.ulcer)}</td>
                <td>
                  {optimizedMetrics.ulcerIndex.toFixed(2)}
                  {renderBadge('ulcer', optimizedMetrics.ulcerIndex)}
                </td>
                <td>{benchmarkMetrics.ulcerIndex.toFixed(2)}</td>
              </tr>
              <tr>
                <td>{t("backtest.upi") || "회복력 지수 (UPI)"} {renderTooltip(explanations.upi)}</td>
                <td>
                  {optimizedMetrics.upi.toFixed(2)}
                  {renderBadge('upi', optimizedMetrics.upi)}
                </td>
                <td>{benchmarkMetrics.upi.toFixed(2)}</td>
              </tr>
              <tr>
                <td>{t("backtest.avgDrawdown") || "평균 하락폭 (Avg. DD)"} {renderTooltip(explanations.avgDrawdown)}</td>
                <td>{(optimizedMetrics.avgDrawdown * 100).toFixed(2)}%</td>
                <td>{(benchmarkMetrics.avgDrawdown * 100).toFixed(2)}%</td>
              </tr>
              <tr>
                <td>{t("backtest.avgRecovery") || "평균 회복 기간"} {renderTooltip(explanations.avgRecovery)}</td>
                <td>
                  {optimizedMetrics.avgRecoveryDays} days
                  {renderBadge('recovery', optimizedMetrics.avgRecoveryDays)}
                </td>
                <td>{benchmarkMetrics.avgRecoveryDays} days</td>
              </tr>
            </>
          )}

        </tbody>
      </table>
    </div>
  );
}

export default PerformanceTableBacktest;